import { combinedAppReducers, CombinedAppReducersType } from '@state/slices/app/combinedAppReducers';
import { basketReducer } from '@state/basket';
import { filtersReducer } from '@state/slices/filters/filterSlice';
import { combinedSearchResultsReducers } from '@state/search-results/combinedSearchResultsReducers';
import { combineReducers, Reducer } from 'redux';
import { createMigrate, persistReducer } from 'redux-persist';
import localStorage from 'redux-persist/lib/storage';
import sessionStorage from 'redux-persist/lib/storage/session';
import { cmsReducer } from '../cms/cmsOperations';
import { navigationReducer } from '@state/slices/navigation/navigationSlice';
import { combinedSearchReducers } from '@state/slices/search/combinedSearchReducers';
import { guestsReducer } from '@state/slices/guests/guestSlice';
import { recentlyViewedReducer } from '@state/slices/recently-viewed';
import { extrasReducer } from '@state/slices/extras';
import { extrasOptionsReducer } from '@state/slices/extras-options';
import { seatMapsReducer } from '@state/slices/seat-maps';
import { flightsSortReducer } from '@state/slices/flights-sort';
import { promoCodeReducer } from '@state/slices/promo-code/promoCodeSlice';
import { paymentFormReducer } from '@state/slices/payment/paymentFormSlice';
import { hotelReducer } from '@state/slices/hotel';
import { combinedPriceCalendarReducers } from '@state/slices/price-calendar/combinedPriceCalendarReducers';
import { mmbReducer } from '@state/slices/mmb';
import { agentReducer } from '@state/slices/agent/agentSlice';
import { dealFinderReducer } from '@state/slices/deal-finder/dealFinderSlice';
import { dealFinderResultsReducer } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { algoliaReducer } from '@state/slices/algolia';
import { dealFinderLastSearchedReducer } from '@state/slices/deal-finder-last-searched/dealFinderLastSearchedSlice';
import { mediaPostReducer } from '@state/slices/agent';
import { promoBannerReducer } from '@state/slices/promo-banner/promoBannerSlice';
import { combinedToursReducers, CombinedToursReducersType } from '@state/slices/tours/combinedTourReducers';
import { destinationFaqReducer } from '@state/slices/destination-faq';
import { mmbDocumentsReducer } from '@state/slices/mmb/documents';
import { footerReducer } from '@state/slices/content';

export const rootReducer: Reducer = combineReducers({
  navigation: navigationReducer,
  cms: cmsReducer,
  filters: filtersReducer,
  basket: basketReducer,
  extras: extrasReducer,
  extrasOptions: extrasOptionsReducer,
  seatMaps: seatMapsReducer,
  app: combinedAppReducers,
  searchResults: combinedSearchResultsReducers,
  flightsSort: flightsSortReducer,
  search: combinedSearchReducers,
  guests: guestsReducer,
  recentlyViewed: recentlyViewedReducer,
  promoCode: promoCodeReducer,
  paymentForm: paymentFormReducer,
  hotel: hotelReducer,
  priceCalendar: combinedPriceCalendarReducers,
  mmb: mmbReducer,
  agent: agentReducer,
  dealFinder: dealFinderReducer,
  algolia: algoliaReducer,
  dealFinderResults: dealFinderResultsReducer,
  dealFinderLastSearched: dealFinderLastSearchedReducer,
  mediaPosts: mediaPostReducer,
  promoBanner: promoBannerReducer,
  tours: combinedToursReducers,
  destinationFaq: destinationFaqReducer,
  mmbDocuments: mmbDocumentsReducer,
  footer: footerReducer
});

const reduxMigrations = {
  [1]: () => {
    // Clear recentlyViewed due to DS change
    return {
      recentlyViewed: []
    } as any;
  },
  [3]: () => {
    return {
      recentlyViewed: []
    } as any;
  }
};

const dealFinderResultsConfig = {
  key: 'results',
  storage: sessionStorage,
  blacklist: ['loading'],
  version: 0
};

const mmbConfig = {
  key: 'mmb',
  storage: sessionStorage,
  whitelist: ['token', 'bookingId'],
  version: 0
};

const appConfig = {
  key: 'packageReferences',
  storage: sessionStorage,
  whitelist: ['packageReferences', 'history', 'paymentError'],
  version: 0
};

const dealFinderConfig = {
  key: 'search',
  storage: sessionStorage,
  whitelist: ['hotelLandingSearch', 'destinationSearch'],
  version: 0
};

const toursConfig = {
  key: 'tourReferences',
  storage: sessionStorage,
  whitelist: ['tourReferences'],
  version: 0
};

const extrasConfig = {
  key: 'extras',
  storage: sessionStorage,
  version: 0
};

export const localStorageConfig = {
  key: 'local',
  storage: localStorage,
  version: 3,
  migrate: createMigrate(reduxMigrations),
  whitelist: ['recentlyViewed']
};

export const footerConfig = {
  key: 'footer',
  storage: sessionStorage,
  version: 0
};

export const searchConfig = {
  key: 'toursSearch',
  storage: sessionStorage,
  whitelist: ['tours'],
  version: 0
};

const combinedReducers = combineReducers({
  dealFinderResults: persistReducer(dealFinderResultsConfig, dealFinderResultsReducer),
  mmb: persistReducer(mmbConfig, mmbReducer),
  navigation: navigationReducer,
  cms: cmsReducer,
  filters: filtersReducer,
  basket: basketReducer,
  app: persistReducer<CombinedAppReducersType>(appConfig, combinedAppReducers),
  extras: persistReducer(extrasConfig, extrasReducer),
  extrasOptions: extrasOptionsReducer,
  seatMaps: seatMapsReducer,
  searchResults: combinedSearchResultsReducers,
  flightsSort: flightsSortReducer,
  search: persistReducer(searchConfig, combinedSearchReducers),
  guests: guestsReducer,
  recentlyViewed: recentlyViewedReducer,
  promoCode: promoCodeReducer,
  paymentForm: paymentFormReducer,
  hotel: hotelReducer,
  priceCalendar: combinedPriceCalendarReducers,
  agent: agentReducer,
  dealFinder: persistReducer(dealFinderConfig, dealFinderReducer),
  algolia: algoliaReducer,
  dealFinderLastSearched: dealFinderLastSearchedReducer,
  mediaPosts: mediaPostReducer,
  promoBanner: promoBannerReducer,
  tours: persistReducer<CombinedToursReducersType>(toursConfig, combinedToursReducers),
  destinationFaq: destinationFaqReducer,
  mmbDocuments: mmbDocumentsReducer,
  footer: persistReducer(footerConfig, footerReducer)
});

export type CombinedReducersType = ReturnType<typeof combinedReducers>;

export const persistedRootReducer = persistReducer<CombinedReducersType>(localStorageConfig, combinedReducers);
